import React from 'react';
import { Link } from 'react-router-dom';
import useSeo from '../hooks/useSeo';
import './Pages.css';

function Services() {
  useSeo(
    'Rev-Tech Services | Managed IT Support & Cybersecurity',
    'Explore Rev-Tech managed IT services including monitoring, backup, security, cloud support, and service packages for predictable IT operations.',
    {
      ogTitle: 'Rev-Tech Services | Managed IT Support & Cybersecurity',
      ogDescription: 'Explore Rev-Tech managed IT services including monitoring, backup, security, cloud support, and service packages for predictable IT operations.',
      twitterTitle: 'Rev-Tech Services | Managed IT Support & Cybersecurity',
      twitterDescription: 'Explore Rev-Tech managed IT services including monitoring, backup, security, cloud support, and service packages for predictable IT operations.',
      canonical: 'https://www.rev-technology.com/services'
    }
  );

  const services = [
    {
      id: 1,
      title: 'Proactive IT Management',
      description: '24/7 monitoring, maintenance, patching, and performance tuning for desktops, servers, and network devices to prevent issues before they impact your business.',
      link: '/services/proactive-it-management'
    },
    {
      id: 2,
      title: 'Security & Threat Protection',
      description: 'Endpoint protection, threat detection, remediation, and employee security awareness training to keep your environment secure and compliant.',
      link: '/services/security-threat-protection'
    },
    {
      id: 3,
      title: 'Cloud Backup & Recovery',
      description: 'Cloud backup, automated recovery, and disaster recovery planning so your data stays protected and your business can recover quickly from outages.',
      link: '/services/cloud-backup-recovery'
    },
    {
      id: 4,
      title: 'Office 365 & Cloud Productivity',
      description: 'Office 365 administration, cloud file sync, and collaboration support to streamline workflows and maximize cloud value.',
      link: '/services/office-365-cloud-productivity'
    },
    {
      id: 5,
      title: 'Unlimited Support',
      description: 'Unlimited remote and on-site support options, plus a responsive helpdesk that resolves issues fast to keep employees productive.',
      link: '/services/unlimited-support'
    },
    {
      id: 6,
      title: 'Managed Service Plans',
      description: 'Flexible Core, Advanced, and TotalCare plans tailored to your business needs, delivering the right mix of support, security, and continuity.',
      link: '/services/managed-service-plans'
    }
  ];

  return (
    <div className="page">
      <h1>Our Managed IT Services</h1>
      <p className="page-subtitle">We provide managed services designed to protect your technology, support your staff, and keep your business running.</p>

      <div className="content-section">
        <h2>What We Offer</h2>
        <p>Rev-Tech delivers complete managed IT solutions for businesses of any size. From proactive monitoring and security to cloud backup, helpdesk support, and disaster recovery, our services are built to reduce risk, improve efficiency, and provide predictable IT costs.</p>
      </div>

      <div className="services-grid">
        {services.map((service) => (
          <div key={service.id} className="service-card">
            <h3>{service.title}</h3>
            <p>{service.description}</p>
            <Link to={service.link} className="learn-more-btn">Learn More</Link>
          </div>
        ))}
      </div>

      <div className="content-section section-gap-top">
        <h2>Service Packages</h2>
        <p>Choose the level of coverage that fits your business. Our Core, Advanced, and TotalCare plans include increasing levels of support, cloud protection, and security tools to match your growth and risk profile.</p>
      </div>

      <div className="contact-cta-section">
        <p>Ready to choose the right plan for your business? Reach out and let's start your managed service journey.</p>
        <Link to="/contact" className="cta-button">Contact Us</Link>
      </div>

      <div className="package-grid">
        <div className="package-card">
          <h3>Core</h3>
          <p className="package-price">$100 / month per device</p>
          <ul>
            <li>Unlimited remote support during standard business hours (8:00 AM - 5:00 PM EST)</li>
            <li>PC monitoring, maintenance, and patching</li>
            <li>Endpoint protection and antivirus</li>
            <li>Basic cloud backup readiness</li>
            <li>Office 365 administration</li>
            <li>Security threat detection and remediation</li>
          </ul>
        </div>
        <div className="package-card">
          <h3>Advanced</h3>
          <p className="package-price">$140 / month per device</p>
          <ul>
            <li>Everything in Core, plus:</li>
            <li>Cloud backup and disaster recovery support</li>
            <li>Extended support hours</li>
          </ul>
        </div>
        <div className="package-card featured-package">
          <h3>TotalCare</h3>
          <p className="package-price">$175 / month per device</p>
          <ul>
            <li>Everything in Advanced, plus:</li>
            <li>Unlimited 24/7 on-site and remote support</li>
            <li>Security awareness training</li>
            <li>Prioritized response and quarterly strategy reviews</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default Services;
